import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, PHONE_DISPLAY, PHONE_TEL } from "./data";
import { scrollTo } from "./scroll";
import { EASE, Flags } from "./motion";

const Nav = () => {
  const [open, setOpen] = useState(false);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    setTimeout(() => scrollTo(href), open ? 250 : 0);
  };

  return (
    <>
      <header
        data-testid="main-nav"
        className="fixed inset-x-0 top-0 z-50 border-b-2 border-ink bg-paper"
      >
        <div className="mx-auto flex h-16 max-w-[1500px] items-center justify-between px-4 sm:px-6">
          <a
            href="#home"
            onClick={(e) => go(e, "#home")}
            data-testid="nav-logo"
            className="flex items-center gap-2.5 font-display text-xl uppercase tracking-wide sm:text-2xl"
          >
            <span className="barber-pole shrink-0" aria-hidden="true">
              <span className="barber-pole-cap" />
              <span className="barber-pole-glass" />
              <span className="barber-pole-cap" />
            </span>
            <span className="flex items-center gap-2.5">
              Oliver&rsquo;s <Flags text="BARBER SHOP" blueFrom={7} />
            </span>
          </a>

          <nav className="hidden items-center gap-6 lg:flex" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => go(e, l.href)}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="font-mono2 text-[11px] font-bold uppercase tracking-[0.25em] text-ink/70 transition-colors hover:text-rust"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <a
              href={PHONE_TEL}
              data-testid="nav-call-button"
              className="inline-flex items-center gap-2 border-2 border-ink px-4 py-2 font-display text-sm uppercase tracking-wide shadow-hard-sm transition-colors hover:bg-ink hover:text-paper"
            >
              <Phone size={14} /> Call to book
            </a>
            <a
              href="#walkin"
              onClick={(e) => go(e, "#walkin")}
              data-testid="nav-walkin-button"
              className="inline-flex items-center border-2 border-ink bg-ink px-4 py-2 font-display text-sm uppercase tracking-wide text-paper shadow-hard-rust transition-transform duration-150 hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none"
            >
              Walk ins welcome
            </a>
          </div>

          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            className="flex h-11 w-11 items-center justify-center border-2 border-ink lg:hidden"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 flex flex-col justify-center overflow-y-auto bg-ink px-8 pb-28 pt-16 lg:hidden"
          >
            <nav className="flex flex-col gap-2" aria-label="Mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => go(e, l.href)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.5, ease: EASE }}
                  className="border-b border-paper/15 py-4 font-display text-5xl uppercase leading-none text-paper"
                >
                  {l.label}
                </motion.a>
              ))}
            </nav>
            <motion.a
              href={PHONE_TEL}
              data-testid="mobile-menu-call-button"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5, ease: EASE }}
              className="mt-10 inline-flex items-center justify-center gap-3 border-2 border-paper bg-rust px-6 py-5 font-display text-2xl uppercase text-paper"
            >
              <Phone size={22} /> Call {PHONE_DISPLAY}
            </motion.a>
            <p className="mt-6 font-mono2 text-[11px] uppercase tracking-[0.3em] text-paper/50">
              Walk ins preferred — St Kilda VIC
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
